import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';

interface PlayRecordingContextType {
  currentTime: number;
  setCurrentTime: (time: number) => void;
  audioUrl: string | null;
  setAudioUrl: (url: string | null) => void;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  playFromTime: (time: number) => Promise<void>;
  cleanupAudio: () => void;
  triggerPlay: (time: number) => void;
  setPlayButtonClick: (clickFn: () => void) => void;
  clickPlayButton: () => void;
}

const PlayRecordingContext = createContext<
  PlayRecordingContextType | undefined
>(undefined);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [pendingTime, setPendingTime] = useState<number | null>(null);
  const [playTrigger, setPlayTrigger] = useState<number | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playButtonClickRef = useRef<(() => void) | null>(null);

  const playFromTime = useCallback(async (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      if (audioRef.current.paused) {
        await audioRef.current.play();
      }
    }
  }, []);

  const triggerPlay = useCallback((time: number) => {
    setCurrentTime(time);
    setPlayTrigger(time);
  }, []);

  const setPlayButtonClick = useCallback((clickFn: () => void) => {
    playButtonClickRef.current = clickFn;
  }, []);

  const clickPlayButton = useCallback(() => {
    if (playButtonClickRef.current) {
      playButtonClickRef.current();
    }
  }, []);

  const cleanupAudio = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setCurrentTime(0);
    setIsPlaying(false);
    setPendingTime(null);
    setPlayTrigger(null);
    playButtonClickRef.current = null;
  }, [audioUrl]);

  useEffect(() => {
    if (!audioUrl) return;

    const timeToPlay = playTrigger ?? pendingTime ?? currentTime;

    if (timeToPlay >= 0) {
      playFromTime(timeToPlay);

      if (playTrigger !== null) {
        setPlayTrigger(null);
      }
      if (pendingTime !== null) {
        setPendingTime(null);
      }
    }
  }, [audioUrl, playTrigger, pendingTime, currentTime, playFromTime]);

  const value: PlayRecordingContextType = {
    currentTime,
    setCurrentTime,
    audioUrl,
    setAudioUrl,
    isPlaying,
    setIsPlaying,
    audioRef,
    playFromTime,
    cleanupAudio,
    triggerPlay,
    setPlayButtonClick,
    clickPlayButton,
  };

  return (
    <PlayRecordingContext.Provider value={value}>
      {children}
    </PlayRecordingContext.Provider>
  );
};

export const usePlayRecording = () => {
  const context = useContext(PlayRecordingContext);
  if (context === undefined) {
    throw new Error('usePlayRecording must be used within an AudioProvider');
  }
  return context;
};
